import React, { useState, useEffect, useContext } from 'react';
import { api } from './api/api';
import { AuthContext } from '../App';

const TEAM_ROLES = [
    { value: '',            label: 'All teams'   },
    { value: 'FORCE_ONE',   label: 'Force One'   },
    { value: 'BRUTE_FORCE', label: 'Brute Force' },
    { value: 'MIDLAND',     label: 'Midland'     },
    { value: 'SUPERNOVA',   label: 'Supernova'   },
    { value: 'RUSSIA',      label: 'Russia'      },
    { value: 'FRIEND',      label: 'Friend'      },
    { value: 'GUEST',       label: 'Guest'       },
    { value: 'OTHER',       label: 'Other'       },
];

// ── Stat columns ───────────────────────────────────────────────────────────

const STATS = [
    { key: 'overall',        deltaKey: 'overallDelta',        label: 'Overall' },
    { key: 'concentration',  deltaKey: 'concentrationDelta',  label: 'Conc.'   },
    { key: 'talent',         deltaKey: 'talentDelta',         label: 'Talent'  },
    { key: 'aggressiveness', deltaKey: 'aggressivenessDelta', label: 'Aggr.'   },
    { key: 'experience',     deltaKey: 'experienceDelta',     label: 'Exp.'    },
    { key: 'techInsight',    deltaKey: 'techInsightDelta',    label: 'Tech'    },
    { key: 'stamina',        deltaKey: 'staminaDelta',        label: 'Stamina' },
    { key: 'charisma',       deltaKey: 'charismaDelta',       label: 'Charisma' },
    { key: 'motivation',     deltaKey: 'motivationDelta',     label: 'Motiv.'  },
    { key: 'reputation',     deltaKey: 'reputationDelta',     label: 'Rep.'    },
    { key: 'weight',         deltaKey: 'weightDelta',         label: 'Wgt kg'  },
];

// ── Helpers ────────────────────────────────────────────────────────────────

const Delta = ({ v }) => {
    if (v == null) return null;
    const color = v > 0 ? '#16a34a' : '#dc2626';
    return (
        <span style={{ color, fontSize: 10, fontWeight: 700, marginLeft: 3 }}>
            {v > 0 ? `+${v}` : v}
        </span>
    );
};

// ── History table ──────────────────────────────────────────────────────────

const HistoryTable = ({ history }) => (
    <div className="fb-table-wrap ra-table-wrap">
        <table className="fb-table ra-table" style={{ whiteSpace: 'nowrap' }}>
            <thead>
                <tr>
                    <th style={{ minWidth: 90 }}>Race</th>
                    <th style={{ minWidth: 180 }}>Track</th>
                    {STATS.map(s => (
                        <th key={s.key} style={{ minWidth: 72, textAlign: 'center' }}>{s.label}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {history.map(entry => (
                    <tr key={`${entry.season}-${entry.raceNumber}`}>
                        <td style={{ fontWeight: 600, fontSize: 12 }}>
                            S{entry.season} R{entry.raceNumber}
                        </td>
                        <td style={{ color: 'var(--c-text-muted)', fontSize: 12 }}>
                            {entry.trackName ?? '—'}
                        </td>
                        {STATS.map(s => (
                            <td key={s.key} style={{ textAlign: 'center' }}>
                                <span style={{ fontWeight: s.key === 'overall' ? 700 : 400 }}>
                                    {entry[s.key] ?? '—'}
                                </span>
                                <Delta v={entry[s.deltaKey]} />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// ── Main component ─────────────────────────────────────────────────────────

const DriverHistory = () => {
    const { userRole } = useContext(AuthContext);
    const isAdmin = userRole === 'ADMIN';

    const [managers,        setManagers]        = useState([]);
    const [selectedManager, setSelectedManager] = useState(null);
    const [history,         setHistory]         = useState([]);
    const [loading,         setLoading]         = useState(true);
    const [detailLoading,   setDetailLoading]   = useState(false);
    const [error,           setError]           = useState(null);
    const [teamRole,        setTeamRole]        = useState('');

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            setSelectedManager(null);
            setHistory([]);
            try {
                const url = teamRole ? `/driver-history?teamRole=${teamRole}` : '/driver-history';
                const res = await api.get(url);
                setManagers(res.data);
            } catch (err) {
                console.error(err);
                setError('Failed to load managers.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [teamRole]);

    // Load driver history for selected manager
    const selectManager = async (manager) => {
        setSelectedManager(manager);
        setDetailLoading(true);
        try {
            const res = await api.get(`/driver-history/${manager.userId}`);
            setHistory(res.data);
        } catch (err) {
            console.error(err);
            setHistory([]);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleMobileSelect = (e) => {
        const m = managers.find(m => String(m.userId) === e.target.value);
        if (m) selectManager(m);
    };

    // Sidebar styles (reuse RaceAnalysis pattern)
    const S = {
        sidebar: {
            width: 220,
            borderRight: '1px solid var(--c-border)',
            overflowY: 'auto',
            flexShrink: 0,
            background: '#fff',
        },
        sidebarHeader: {
            padding: '12px 16px',
            fontWeight: 700,
            fontSize: 11,
            borderBottom: '1px solid var(--c-border)',
            background: '#fafafa',
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            color: 'var(--c-text-muted)',
        },
        managerItem: (selected) => ({
            padding: '10px 16px',
            cursor: 'pointer',
            background: selected ? '#fff3f3' : 'transparent',
            borderLeft: `3px solid ${selected ? 'var(--c-accent)' : 'transparent'}`,
            fontSize: 13,
            fontWeight: selected ? 600 : 400,
            color: 'var(--c-text)',
            transition: 'background 0.12s',
        }),
        main: {
            flex: 1,
            overflowY: 'auto',
            padding: 24,
            background: 'var(--c-bg)',
            minWidth: 0,
        },
        placeholder: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'var(--c-text-light)',
            gap: 12,
        },
    };

    return (
        <div className="ra-page">
            {/* ── Desktop sidebar ── */}
            <div style={S.sidebar} className="ra-desktop-sidebar">
                <div style={S.sidebarHeader}>Managers</div>
                {isAdmin && (
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--c-border)' }}>
                        <select value={teamRole} onChange={e => setTeamRole(e.target.value)}
                            style={{ width: '100%', padding: '5px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--c-border)', fontSize: 12, background: '#fff', color: 'var(--c-text)', cursor: 'pointer' }}>
                            {TEAM_ROLES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                )}
                {loading && (
                    <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>
                )}
                {error && (
                    <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>
                )}
                {!loading && !error && managers.length === 0 && (
                    <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>No data.</div>
                )}
                {managers.map(m => (
                    <div
                        key={m.userId}
                        style={S.managerItem(selectedManager?.userId === m.userId)}
                        onClick={() => selectManager(m)}
                    >
                        {m.managerName}
                    </div>
                ))}
            </div>

            {/* ── Main panel ── */}
            <div style={S.main} className="ra-main">
                {/* Mobile selector */}
                <div className="ra-mobile-selector">
                    <select
                        className="fb-select"
                        style={{ width: '100%' }}
                        value={selectedManager?.userId ?? ''}
                        onChange={handleMobileSelect}
                        disabled={loading}
                    >
                        <option value="">{loading ? 'Loading…' : '— Select a manager —'}</option>
                        {managers.map(m => (
                            <option key={m.userId} value={m.userId}>{m.managerName}</option>
                        ))}
                    </select>
                </div>

                {/* Placeholder */}
                {!selectedManager && (
                    <div style={S.placeholder} className="ra-desktop-placeholder">
                        <span style={{ fontSize: 44 }}>📈</span>
                        <span style={{ fontSize: 14 }}>Select a manager to view driver history</span>
                    </div>
                )}

                {/* Loading */}
                {selectedManager && detailLoading && (
                    <div style={{ color: 'var(--c-text-muted)', marginTop: 48, textAlign: 'center', fontSize: 14 }}>
                        Loading history…
                    </div>
                )}

                {/* History table */}
                {selectedManager && !detailLoading && (
                    <>
                        <div className="fb-page-header">
                            <div className="fb-page-title">{selectedManager.managerName}</div>
                            <div className="fb-page-sub">
                                Driver characteristics by race — {history.length} record{history.length !== 1 ? 's' : ''}
                            </div>
                        </div>

                        {history.length === 0 ? (
                            <div style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>
                                No data available.
                            </div>
                        ) : (
                            <div className="fb-card">
                                <HistoryTable history={history} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DriverHistory;
